import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'

export type TripDetailSelection =
  | { type: 'waypoint'; id: string }
  | { type: 'crew'; id: string }
  | { type: 'meal'; id: string }
  | { type: 'meal-add' }
  | { type: 'gear'; id: string }
  | { type: 'gear-add' }
  | { type: 'announcement-add' }
  | { type: 'trip-feed-add' }
  | null

interface TripDetailPanelContextValue {
  selectedDetail: TripDetailSelection
  setSelectedDetail: (detail: TripDetailSelection) => void
  closePanel: () => void
  /** Ref for callback when a waypoint is deleted/updated in the panel (e.g. so TripDetail can refresh). */
  onWaypointDeletedRef: React.MutableRefObject<(() => void | Promise<void>) | null>
}

const TripDetailPanelContext = createContext<TripDetailPanelContextValue | null>(null)

export function TripDetailPanelProvider({ children }: { children: ReactNode }) {
  const [selectedDetail, setSelectedDetail] = useState<TripDetailSelection>(null)
  const onWaypointDeletedRef = useRef<(() => void | Promise<void>) | null>(null)

  const closePanel = useCallback(() => {
    setSelectedDetail(null)
  }, [])

  const value: TripDetailPanelContextValue = {
    selectedDetail,
    setSelectedDetail,
    closePanel,
    onWaypointDeletedRef
  }

  return (
    <TripDetailPanelContext.Provider value={value}>
      {children}
    </TripDetailPanelContext.Provider>
  )
}

export function useTripDetailPanel() {
  const ctx = useContext(TripDetailPanelContext)
  if (!ctx) {
    throw new Error('useTripDetailPanel must be used within TripDetailPanelProvider')
  }
  return ctx
}

export function useTripDetailPanelOptional() {
  return useContext(TripDetailPanelContext)
}
